/**
 * Restoration recipe — serialise the entire user state (preset + module
 * on/off + module params + spectral-repair regions) to a portable JSON
 * blob, save it as a .json download, restore via file upload OR the most
 * recent recipe via localStorage on next visit.
 *
 * Use case: archivist digitising 30 cassette tapes from the same source
 * sets up a chain once, exports the recipe, applies to each subsequent
 * file without re-clicking the whole UI. Power-user "save as default"
 * also lands here.
 *
 * Recipe schema (v1):
 *   {
 *     v: 1,
 *     slug: 'audio-restoration',
 *     created: '2026-05-15T16:42:11Z',
 *     name: 'My Vinyl Cleanup',
 *     preset: 'vinyl-33-45',
 *     modulesEnabled: { derumble: true, dehum: true, declick: true, ... },
 *     userParams: { derumble: {cutoffHz: 40, slopeDb: 24}, ... },
 *     spectralRegions: [ {x1Norm, y1Norm, x2Norm, y2Norm}, ... ]   // pixel coords normalised to [0,1] so they work on any future canvas size
 *   }
 *
 * Storage:
 *   - explicit download → .json file
 *   - localStorage key `audio-restoration:lastRecipe` (auto-saved on every Run,
 *     auto-restored on next visit unless user clears)
 *
 * Forwards-compat: future versions bump `v` and gracefully degrade older
 * recipes (missing fields default to current schema defaults).
 */
(function () {
    'use strict';

    const STORAGE_KEY = 'audio-restoration:lastRecipe';
    const SCHEMA_VERSION = 1;

    function captureRecipe(state) {
        // state is supplied by the blade: { currentPreset, moduleState, userParams, spectralRegionsNormalised }
        return {
            v: SCHEMA_VERSION,
            slug: 'audio-restoration',
            created: new Date().toISOString(),
            name: state.name || 'Untitled recipe',
            preset: state.currentPreset || 'custom',
            modulesEnabled: Object.assign({}, state.moduleState || {}),
            userParams: JSON.parse(JSON.stringify(state.userParams || {})), // deep clone
            spectralRegions: Array.isArray(state.spectralRegionsNormalised)
                ? state.spectralRegionsNormalised.slice()
                : [],
        };
    }

    function applyRecipe(recipe, hooks) {
        // hooks is supplied by the blade: { setPreset(slug), setModuleToggle(key, on),
        //                                   setParam(mod, param, value), setSpectralRegions(arr) }
        if (!recipe || recipe.v !== SCHEMA_VERSION) {
            throw new Error('Unsupported recipe version: ' + (recipe && recipe.v));
        }
        if (recipe.slug !== 'audio-restoration') {
            throw new Error('Recipe is for a different tool: ' + recipe.slug);
        }
        // Apply in stable order: preset cascade FIRST (sets defaults), then
        // overrides from saved userParams + module toggles. Spectral regions
        // last because they need the spectral panel context to be open.
        if (recipe.preset && hooks.setPreset) hooks.setPreset(recipe.preset);
        if (recipe.modulesEnabled && hooks.setModuleToggle) {
            Object.keys(recipe.modulesEnabled).forEach(function (k) {
                hooks.setModuleToggle(k, !!recipe.modulesEnabled[k]);
            });
        }
        if (recipe.userParams && hooks.setParam) {
            Object.keys(recipe.userParams).forEach(function (mod) {
                const params = recipe.userParams[mod] || {};
                Object.keys(params).forEach(function (p) {
                    hooks.setParam(mod, p, params[p]);
                });
            });
        }
        if (recipe.spectralRegions && hooks.setSpectralRegions) {
            hooks.setSpectralRegions(recipe.spectralRegions);
        }
    }

    function downloadJson(recipe, filename) {
        const blob = new Blob([JSON.stringify(recipe, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || ((recipe.name || 'recipe').replace(/[^\w\-]/g, '_') + '.json');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
    }

    function readJsonFile(file) {
        return new Promise(function (resolve, reject) {
            const r = new FileReader();
            r.onload = function () {
                try { resolve(JSON.parse(r.result)); }
                catch (e) { reject(new Error('Invalid JSON: ' + e.message)); }
            };
            r.onerror = function () { reject(r.error || new Error('Read failed')); };
            r.readAsText(file);
        });
    }

    function saveToLocalStorage(recipe) {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(recipe)); }
        catch (_) { /* quota or private-mode — ignore */ }
    }
    function loadFromLocalStorage() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch (_) { return null; }
    }
    function clearLocalStorage() {
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    }

    window.RestorationRecipe = {
        VERSION: SCHEMA_VERSION,
        capture: captureRecipe,
        apply: applyRecipe,
        downloadJson: downloadJson,
        readJsonFile: readJsonFile,
        saveToLocalStorage: saveToLocalStorage,
        loadFromLocalStorage: loadFromLocalStorage,
        clearLocalStorage: clearLocalStorage,
    };
})();
